@file:OptIn(ExperimentalMaterial3Api::class)

package pl.zar.tikthrow.ui.screen

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.selection.SelectionContainer
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material.icons.filled.Refresh
import androidx.compose.material.icons.outlined.Key
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import pl.zar.tikthrow.shared.dto.ApiKeyInfo
import pl.zar.tikthrow.ui.theme.*
import pl.zar.tikthrow.viewmodel.ApiKeysViewModel

private val gradientBrush = Brush.linearGradient(listOf(GradientStart, GradientEnd))
// dark theme (Slate50 = near-black): a red that stays readable on a dark surface.
private val warningRed = Color(0xFFEF5350)

/**
 * T201 (spec 002-api-mcp, US1): the API-key screen — the human entry point of
 * the whole agent flow. The owner mints a key here, copies the raw value exactly
 * once, and revokes it when it leaks or is no longer needed.
 *
 * Contract: `POST /api/v1/keys` (raw key returned once), `GET /api/v1/keys`
 * (masked metadata, cursor-paginated), `DELETE /api/v1/keys/{id}`. Key
 * management is session-only — see [ApiKeysViewModel].
 */
@Composable
fun ApiKeysScreen(
    viewModel: ApiKeysViewModel,
    onBack: () -> Unit,
) {
    val state by viewModel.state.collectAsState()
    var showCreate by remember { mutableStateOf(false) }
    var name by remember { mutableStateOf("") }
    var pendingRevoke by remember { mutableStateOf<ApiKeyInfo?>(null) }

    Surface(modifier = Modifier.fillMaxSize(), color = MaterialTheme.colorScheme.background) {
        Column(modifier = Modifier.fillMaxSize()) {
            // Top bar — same shape as the other pushed screens.
            Surface(color = Slate100, shadowElevation = 2.dp) {
                Row(
                    modifier = Modifier.fillMaxWidth().padding(horizontal = 8.dp, vertical = 8.dp),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    IconButton(onClick = onBack) {
                        Icon(Icons.Default.ArrowBack, contentDescription = "Wstecz", tint = Slate600)
                    }
                    Column(modifier = Modifier.weight(1f)) {
                        Text(
                            text = "Klucze API",
                            style = TextStyle(brush = gradientBrush),
                            fontSize = 20.sp,
                            fontWeight = FontWeight.Black,
                        )
                        Text("dostęp dla agentów przez /api/v1", fontSize = 12.sp, color = Slate500)
                    }
                    IconButton(onClick = { viewModel.refresh() }) {
                        Icon(Icons.Filled.Refresh, contentDescription = "Odśwież", tint = Slate500)
                    }
                }
            }

            state.error?.let { message ->
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 16.dp, vertical = 8.dp),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Text(message, fontSize = 13.sp, color = warningRed, modifier = Modifier.weight(1f))
                    TextButton(onClick = { viewModel.dismissError() }) { Text("OK") }
                }
            }

            Box(modifier = Modifier.weight(1f).fillMaxWidth()) {
                when {
                    state.loading && state.keys.isEmpty() -> Box(
                        modifier = Modifier.fillMaxSize(),
                        contentAlignment = Alignment.Center,
                    ) { CircularProgressIndicator(color = GradientStart) }

                    state.keys.isEmpty() -> Column(
                        modifier = Modifier.fillMaxSize().padding(28.dp),
                        horizontalAlignment = Alignment.CenterHorizontally,
                        verticalArrangement = Arrangement.Center,
                    ) {
                        Icon(Icons.Outlined.Key, contentDescription = null, tint = Slate300, modifier = Modifier.size(52.dp))
                        Spacer(Modifier.height(12.dp))
                        Text("Brak kluczy API", fontSize = 17.sp, fontWeight = FontWeight.Bold, color = Slate800)
                        Spacer(Modifier.height(6.dp))
                        Text(
                            "Wygeneruj klucz, żeby agent (np. ai-marketer) mógł działać w Twoim imieniu przez /api/v1.",
                            fontSize = 13.sp,
                            color = Slate500,
                        )
                    }

                    else -> LazyColumn(
                        modifier = Modifier.fillMaxSize(),
                        contentPadding = PaddingValues(horizontal = 16.dp, vertical = 12.dp),
                        verticalArrangement = Arrangement.spacedBy(10.dp),
                    ) {
                        item {
                            Text(
                                "Klucz pokazujemy w całości tylko raz, przy generowaniu. Serwer trzyma sam hash.",
                                fontSize = 12.sp,
                                color = Slate500,
                            )
                        }
                        items(state.keys, key = { it.id }) { key ->
                            KeyRow(key = key, onRevoke = { pendingRevoke = key })
                        }
                    }
                }
            }

            Surface(color = Slate100, shadowElevation = 2.dp) {
                Button(
                    onClick = { name = ""; showCreate = true },
                    enabled = !state.creating,
                    shape = RoundedCornerShape(4.dp),
                    colors = ButtonDefaults.buttonColors(containerColor = GradientStart),
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(16.dp),
                ) {
                    Text("Wygeneruj nowy klucz", fontSize = 15.sp, fontWeight = FontWeight.SemiBold)
                }
            }
        }
    }

    if (showCreate) {
        AlertDialog(
            onDismissRequest = { showCreate = false },
            title = { Text("Nowy klucz API") },
            text = {
                Column {
                    Text(
                        "Nazwij klucz tak, żebyś wiedział, do czego należy (np. „ai-marketer”).",
                        fontSize = 13.sp,
                        color = Slate600,
                    )
                    Spacer(Modifier.height(10.dp))
                    OutlinedTextField(
                        value = name,
                        onValueChange = { name = it },
                        label = { Text("Nazwa klucza") },
                        singleLine = true,
                        modifier = Modifier.fillMaxWidth(),
                        shape = RoundedCornerShape(4.dp),
                    )
                }
            },
            confirmButton = {
                TextButton(
                    onClick = { viewModel.create(name); showCreate = false },
                    enabled = name.isNotBlank() && !state.creating,
                ) { Text("Wygeneruj") }
            },
            dismissButton = {
                TextButton(onClick = { showCreate = false }) { Text("Anuluj") }
            },
        )
    }

    state.freshKey?.let { rawKey ->
        AlertDialog(
            onDismissRequest = { viewModel.dismissFreshKey() },
            title = { Text("Klucz wygenerowany") },
            text = {
                Column {
                    Text(
                        "Skopiuj go TERAZ — pokazujemy go tylko raz i nie da się go odzyskać.",
                        fontSize = 13.sp,
                        fontWeight = FontWeight.SemiBold,
                        color = warningRed,
                    )
                    Spacer(Modifier.height(10.dp))
                    // SelectionContainer: zaznacz i skopiuj systemowo (każdy target).
                    SelectionContainer {
                        Text(
                            text = rawKey,
                            fontFamily = FontFamily.Monospace,
                            fontSize = 12.sp,
                            color = Slate900,
                            modifier = Modifier
                                .fillMaxWidth()
                                .clip(RoundedCornerShape(4.dp))
                                .background(Slate50)
                                .border(1.dp, Slate200, RoundedCornerShape(4.dp))
                                .padding(10.dp),
                        )
                    }
                    Spacer(Modifier.height(8.dp))
                    Text(
                        "Użycie: nagłówek „Authorization: Bearer <klucz>” na https://tikthrower.zarnowiec.fr/api/v1",
                        fontSize = 11.sp,
                        color = Slate500,
                    )
                }
            },
            confirmButton = {
                TextButton(onClick = { viewModel.dismissFreshKey() }) { Text("Zamknij") }
            },
        )
    }

    pendingRevoke?.let { key ->
        AlertDialog(
            onDismissRequest = { pendingRevoke = null },
            title = { Text("Odwołać klucz?") },
            text = {
                Text(
                    "Klucz „${key.name}” przestanie działać natychmiast. Tej operacji nie można cofnąć.",
                    fontSize = 13.sp,
                    color = Slate600,
                )
            },
            confirmButton = {
                TextButton(onClick = { viewModel.revoke(key.id); pendingRevoke = null }) {
                    Text("Odwołaj", color = warningRed)
                }
            },
            dismissButton = {
                TextButton(onClick = { pendingRevoke = null }) { Text("Anuluj") }
            },
        )
    }
}

/** One key: who it is, when it was made, whether it is still usable. */
@Composable
private fun KeyRow(key: ApiKeyInfo, onRevoke: () -> Unit) {
    Surface(shape = RoundedCornerShape(6.dp), color = Slate100, modifier = Modifier.fillMaxWidth()) {
        Column(modifier = Modifier.fillMaxWidth().padding(14.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Icon(Icons.Outlined.Key, contentDescription = null, tint = Slate500, modifier = Modifier.size(18.dp))
                Spacer(Modifier.width(8.dp))
                Text(
                    text = key.name,
                    fontSize = 15.sp,
                    fontWeight = FontWeight.SemiBold,
                    color = Slate900,
                    modifier = Modifier.weight(1f),
                )
                Text(statusLabel(key), fontSize = 11.sp, fontWeight = FontWeight.SemiBold, color = statusColor(key))
            }
            Spacer(Modifier.height(6.dp))
            Text("utworzono ${formatDateMillis(key.createdAt.toEpochMilliseconds())}", fontSize = 12.sp, color = Slate500)
            Text(
                key.lastUsedAt?.let { "ostatnio użyty ${formatDateMillis(it.toEpochMilliseconds())}" }
                    ?: "jeszcze nieużywany",
                fontSize = 12.sp,
                color = Slate500,
            )
            key.expiresAt?.let { expires ->
                Text("wygasa ${formatDateMillis(expires.toEpochMilliseconds())}", fontSize = 12.sp, color = Slate500)
            }
            if (key.requireReview) {
                Text("publikacje tym kluczem czekają na zatwierdzenie", fontSize = 11.sp, color = Slate500)
            }
            Text("uprawnienia: ${key.scopes.joinToString(", ")}", fontSize = 11.sp, color = Slate500)
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.End,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                TextButton(onClick = onRevoke, enabled = key.revokedAt == null) {
                    Icon(Icons.Filled.Delete, contentDescription = null, modifier = Modifier.size(16.dp))
                    Spacer(Modifier.width(6.dp))
                    Text("Odwołaj", fontSize = 13.sp)
                }
            }
        }
    }
}

private fun statusLabel(key: ApiKeyInfo): String = when {
    key.revokedAt != null -> "ODWOŁANY"
    key.expiresAt?.let { it <= kotlin.time.Clock.System.now() } == true -> "WYGASŁ"
    else -> "AKTYWNY"
}

private fun statusColor(key: ApiKeyInfo): Color = when {
    key.revokedAt != null -> warningRed
    key.expiresAt?.let { it <= kotlin.time.Clock.System.now() } == true -> Slate500
    else -> GradientStart
}
